import { useQuery } from "@tanstack/react-query"
import { type ReactNode, useId } from "react"

import { api } from "@/api/cliente"
import type { Empresa } from "@/componentes/empresa/tipos"
import { FileteOro, MembreteImpreso, PieMembrete } from "@/componentes/impresos/MembreteImpreso"

import { cuerpoQueEntra } from "./ajuste"
import type { DatosGuia } from "./tipos"
import { NOMBRES } from "./tipos"

/**
 * La guía de despacho: el papel con el que la mercancía sale del puerto.
 *
 * ⚠ **Es el único formato de la biblioteca que es NUESTRO, y por eso se maqueta como
 * el resto del sistema.** Los otros dos reproducen un PDF de la aduana y van
 * posicionados en absoluto, al punto, porque hay un original que respetar. Aquí el
 * original es un documento de Word de la propia agencia: copiar sus coordenadas sería
 * heredar las de un procesador de textos —sangrías de 3 cm, una tabla que empuja la
 * dirección debajo de su rótulo— en vez de componerlo. Lo que se conserva es lo que
 * el documento DICE: sus campos, sus palabras y su orden.
 *
 * ⚠ **Lleva la tinta y la marca de la agencia**, al revés que los formatos del
 * SENIAT: vino, filete dorado y el logotipo vectorial (`MembreteImpreso`). Un formato
 * oficial tiene que verse como el que publica el organismo; un papel nuestro, como la
 * firma de nuestros correos.
 *
 * ⚠ **Se corrigió la ortografía del original.** El Word dice «GUIA», «DIRECCION»,
 * «MERCANCIA» y «CEDULA». En un formato de la aduana las faltas se conservan —ver
 * `PieResponsable`—, pero eso es porque el documento no es nuestro. Éste sí, y se
 * entrega a un transportista con el membrete de la agencia encima.
 *
 * ⚠ **El cuerpo de letra de cada campo baja solo cuando lo escrito no cabe**
 * (`ajuste.ts`), igual que en los formatos oficiales: un destino como «ZONA INDUSTRIAL
 * SANTA CRUZ EDO ARAGUA» no entra a cuerpo de diseño en una celda de tercio de hoja, y
 * un `input` no avisa de lo que recorta — enseña el tramo que cabe y esconde el resto.
 *
 * ⚠ **No hay campo de firma.** Se firma y se sella a mano, como el resto de lo que
 * sale en papel desde este sistema.
 */

/** El margen del papel, en mm. Nuestro, no el del Word: aquél dejaba 3 cm a los lados. */
const MARGEN = { lados: 18, arriba: 14, abajo: 13 }

/** El cuerpo de los datos tecleados, en pt. */
const CUERPO = 10.5

/** De milímetros a puntos: `ajuste.ts` mide huecos en pt y aquí todo está en mm. */
function aPuntos(mm: number): number {
  return (mm * 72) / 25.4
}

/**
 * Un dato de la guía: su rótulo encima y lo tecleado debajo, sobre una línea fina.
 *
 * ⚠ **El rótulo va ENCIMA y no al lado.** En el Word van en la misma línea, separados
 * por una tirada de espacios que es lo que los alinea; en cuanto el nombre de un
 * cliente crece, la columna de la derecha se desplaza. Encima, cada dato ocupa todo su
 * ancho y las dos columnas no pueden descuadrarse.
 */
function Dato({
  rotulo,
  valor,
  onCambio,
  ancho,
  cifra = false,
}: {
  rotulo: string
  valor: string
  onCambio: (valor: string) => void
  /** El ancho útil del campo en mm, para saber a qué cuerpo cabe lo escrito. */
  ancho: number
  cifra?: boolean
}) {
  const ajuste = cuerpoQueEntra(valor, aPuntos(ancho), CUERPO)

  return (
    <label style={{ display: "block", minWidth: 0 }}>
      <span
        style={{
          display: "block",
          fontSize: "2.5mm",
          fontWeight: 700,
          letterSpacing: "0.35mm",
          textTransform: "uppercase",
          color: "var(--impreso-vino)",
        }}
      >
        {rotulo}
      </span>
      <input
        type="text"
        value={valor}
        onChange={(evento) => onCambio(evento.target.value)}
        className={`campo-plantilla ${ajuste.cabe ? "" : "campo-plantilla-no-cabe"} ${cifra ? "tabular" : ""}`}
        style={{
          display: "block",
          width: "100%",
          marginTop: "0.8mm",
          paddingBottom: "0.9mm",
          borderBottom: "0.25mm solid var(--impreso-linea)",
          fontSize: `${(ajuste.cuerpo * 25.4) / 72}mm`,
          lineHeight: 1.3,
          color: "var(--impreso-tinta)",
        }}
      />
    </label>
  )
}

/**
 * Una de las tres tablas del pie: tres rótulos en banda y sus tres valores debajo.
 *
 * ⚠ **La banda se IMPRIME en vino.** `.pieza-impresa` fuerza `print-color-adjust:
 * exact` para que el navegador no la quite «para ahorrar tinta»: sin ella las tres
 * tablas se leen como un bloque continuo de texto y hay que contar columnas para saber
 * cuál es la placa y cuál la cédula.
 */
function TablaTres({
  columnas,
  anchoCelda,
}: {
  columnas: { rotulo: string; valor: string; onCambio: (valor: string) => void }[]
  anchoCelda: number
}) {
  // ⚠ **El rótulo de la banda ES la etiqueta del campo, con `htmlFor`.** Van en dos
  // rejillas distintas —la banda arriba y los valores debajo— así que no se pueden
  // anidar; el primer intento fue un `sr-only` dentro del campo, y entonces el mismo
  // texto estaba dos veces: quien usa un lector oía «Teléfono, Teléfono». El `id` se
  // genera por instancia porque de estas tablas hay tres en la misma hoja.
  const base = useId()

  return (
    <div style={{ border: "0.3mm solid var(--impreso-linea)" }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr" }}>
        {columnas.map((columna, indice) => (
          <label
            key={columna.rotulo}
            htmlFor={`${base}-${indice}`}
            style={{
              padding: "1.2mm 2mm",
              fontSize: "2.5mm",
              fontWeight: 700,
              letterSpacing: "0.35mm",
              textAlign: "center",
              textTransform: "uppercase",
              background: "var(--impreso-vino)",
              color: "var(--impreso-papel)",
            }}
          >
            {columna.rotulo}
          </label>
        ))}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr" }}>
        {columnas.map((columna, indice) => {
          const ajuste = cuerpoQueEntra(columna.valor, aPuntos(anchoCelda - 4), CUERPO)
          return (
            <input
              key={columna.rotulo}
              id={`${base}-${indice}`}
              type="text"
              value={columna.valor}
              onChange={(evento) => columna.onCambio(evento.target.value)}
              className={`campo-plantilla ${ajuste.cabe ? "" : "campo-plantilla-no-cabe"}`}
              style={{
                display: "block",
                width: "100%",
                minWidth: 0,
                padding: "1.8mm 2mm",
                // La primera no lleva filo: el borde de la tabla ya está ahí.
                borderLeft: indice === 0 ? undefined : "0.25mm solid var(--impreso-linea)",
                fontSize: `${(ajuste.cuerpo * 25.4) / 72}mm`,
                textAlign: "center",
                color: "var(--impreso-tinta)",
              }}
            />
          )
        })}
      </div>
    </div>
  )
}

function Fila({ children }: { children: ReactNode }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4mm 7mm" }}>
      {children}
    </div>
  )
}

export function GuiaDespacho({
  datos,
  onCambio,
  empresa,
}: {
  datos: DatosGuia
  onCambio: (cambio: Partial<DatosGuia>) => void
  /** Si no se pasa, la pide él: así el pliego de impresión no depende de la pantalla. */
  empresa?: Empresa
}) {
  const consulta = useQuery({
    queryKey: ["empresa"],
    queryFn: () => api.get<Empresa>("/api/v1/empresa"),
    enabled: empresa === undefined,
  })
  const agencia = empresa ?? consulta.data

  const util = 216 - MARGEN.lados * 2
  const columna = (util - 7) / 2
  const celda = util / 3

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        flexDirection: "column",
        padding: `${MARGEN.arriba}mm ${MARGEN.lados}mm ${MARGEN.abajo}mm`,
      }}
    >
      {/* Sin configuración de empresa todavía no hay membrete, pero la guía se puede
          rellenar igual: lo que falta es la cabecera, no el documento. */}
      {agencia && <MembreteImpreso empresa={agencia} />}
      <div style={{ marginTop: "2.5mm" }}>
        <FileteOro />
      </div>

      <div style={{ marginTop: "6mm", textAlign: "center" }}>
        <h2
          style={{
            margin: 0,
            fontSize: "6.2mm",
            fontWeight: 700,
            letterSpacing: "1.1mm",
            textTransform: "uppercase",
            color: "var(--impreso-vino)",
          }}
        >
          {NOMBRES.GUIA_DESPACHO}
        </h2>
        <label
          style={{
            display: "inline-flex",
            alignItems: "baseline",
            gap: "1.5mm",
            marginTop: "1.5mm",
          }}
        >
          <span style={{ fontSize: "3mm", fontWeight: 600, color: "var(--impreso-gris)" }}>
            N.º
          </span>
          <input
            type="text"
            value={datos.numero}
            onChange={(evento) => onCambio({ numero: evento.target.value })}
            className="campo-plantilla tabular"
            style={{
              width: "60mm",
              paddingBottom: "0.7mm",
              borderBottom: "0.25mm solid var(--impreso-linea)",
              fontSize: "3.6mm",
              fontWeight: 600,
              textAlign: "center",
              color: "var(--impreso-tinta)",
            }}
          />
        </label>
      </div>

      {/* El lugar y la fecha, a la derecha, como se encabeza una carta. */}
      <p
        style={{
          margin: "5mm 0 0",
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "baseline",
          gap: "1.5mm",
          fontSize: "3mm",
          color: "var(--impreso-tinta)",
        }}
      >
        <label style={{ display: "contents" }}>
          <span className="sr-only">Lugar</span>
          <input
            type="text"
            value={datos.lugar}
            onChange={(evento) => onCambio({ lugar: evento.target.value })}
            className="campo-plantilla"
            style={{ width: "38mm", textAlign: "right", fontSize: "3mm" }}
          />
        </label>
        <span aria-hidden={true}>,</span>
        <label style={{ display: "contents" }}>
          <span className="sr-only">Fecha</span>
          <input
            type="text"
            value={datos.fecha}
            onChange={(evento) => onCambio({ fecha: evento.target.value })}
            className="campo-plantilla"
            style={{ width: "52mm", fontSize: "3mm" }}
          />
        </label>
      </p>

      <div style={{ marginTop: "5mm", display: "flex", flexDirection: "column", gap: "4mm" }}>
        <Fila>
          <Dato
            rotulo="Expediente"
            valor={datos.expediente}
            onCambio={(expediente) => onCambio({ expediente })}
            ancho={columna}
            cifra
          />
          <Dato
            rotulo="Buque y fecha de llegada"
            valor={datos.buque}
            onCambio={(buque) => onCambio({ buque })}
            ancho={columna}
          />
        </Fila>
        <Fila>
          <Dato
            rotulo="Cliente"
            valor={datos.cliente}
            onCambio={(cliente) => onCambio({ cliente })}
            ancho={columna}
          />
          <Dato rotulo="B/L" valor={datos.bl} onCambio={(bl) => onCambio({ bl })} ancho={columna} />
        </Fila>
        <Fila>
          <Dato
            rotulo="RIF"
            valor={datos.rif}
            onCambio={(rif) => onCambio({ rif })}
            ancho={columna}
            cifra
          />
          <Dato
            rotulo="Mercancía"
            valor={datos.mercancia}
            onCambio={(mercancia) => onCambio({ mercancia })}
            ancho={columna}
          />
        </Fila>
        <Dato
          rotulo="Dirección de entrega"
          valor={datos.direccion}
          onCambio={(direccion) => onCambio({ direccion })}
          ancho={util}
        />
      </div>

      <div style={{ marginTop: "7mm", display: "flex", flexDirection: "column", gap: "4mm" }}>
        <TablaTres
          anchoCelda={celda}
          columnas={[
            {
              rotulo: "Cantidad",
              valor: datos.carga.cantidad,
              onCambio: (cantidad) => onCambio({ carga: { ...datos.carga, cantidad } }),
            },
            {
              rotulo: "Contenedor y precinto",
              valor: datos.carga.contenedor,
              onCambio: (contenedor) => onCambio({ carga: { ...datos.carga, contenedor } }),
            },
            {
              rotulo: "Destino",
              valor: datos.carga.destino,
              onCambio: (destino) => onCambio({ carga: { ...datos.carga, destino } }),
            },
          ]}
        />
        <TablaTres
          anchoCelda={celda}
          columnas={[
            {
              rotulo: "Conductor",
              valor: datos.conductor.nombre,
              onCambio: (nombre) => onCambio({ conductor: { ...datos.conductor, nombre } }),
            },
            {
              rotulo: "Cédula",
              valor: datos.conductor.cedula,
              onCambio: (cedula) => onCambio({ conductor: { ...datos.conductor, cedula } }),
            },
            {
              rotulo: "Teléfono",
              valor: datos.conductor.telefono,
              onCambio: (telefono) => onCambio({ conductor: { ...datos.conductor, telefono } }),
            },
          ]}
        />
        <TablaTres
          anchoCelda={celda}
          columnas={[
            {
              rotulo: "Modelo",
              valor: datos.vehiculo.modelo,
              onCambio: (modelo) => onCambio({ vehiculo: { ...datos.vehiculo, modelo } }),
            },
            {
              rotulo: "Color",
              valor: datos.vehiculo.color,
              onCambio: (color) => onCambio({ vehiculo: { ...datos.vehiculo, color } }),
            },
            {
              rotulo: "Placa",
              valor: datos.vehiculo.placa,
              onCambio: (placa) => onCambio({ vehiculo: { ...datos.vehiculo, placa } }),
            },
          ]}
        />
      </div>

      {/* ⚠ **Las dos firmas se AÑADEN; el Word no las tenía.** Una guía de despacho se
          entrega en mano y lo que la cierra es el acuse del transportista: sin un sitio
          donde firmar, el papel prueba que la mercancía salió pero no que alguien la
          recibió. Si la agencia prefiere el formato tal cual, se quitan estas ocho
          líneas y no arrastran nada más.

          ⚠ **No tienen campo que teclear**, como en los formatos oficiales: una rúbrica
          tecleada no vale nada, y aquí además hay que sellar. El hueco empuja al pie
          hacia abajo con `flex: 1`, así que la hoja se reparte sola aunque un día se
          añada un renglón arriba. */}
      <div
        style={{
          flex: 1,
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          gap: "12mm",
          marginTop: "10mm",
          paddingBottom: "6mm",
        }}
      >
        {["Por la agencia", "Recibí conforme"].map((pie) => (
          <div key={pie} style={{ flex: 1, textAlign: "center" }}>
            <div style={{ borderTop: "0.3mm solid var(--impreso-tinta)" }} />
            <span
              style={{
                display: "block",
                marginTop: "1.5mm",
                fontSize: "2.6mm",
                letterSpacing: "0.3mm",
                textTransform: "uppercase",
                color: "var(--impreso-gris)",
              }}
            >
              {pie}
            </span>
          </div>
        ))}
      </div>

      {agencia && <PieMembrete empresa={agencia} />}
    </div>
  )
}
